(ns minorodata.sandbox.events
  (:require
   [clojure.set :as c.set]
   [clojure.string :as str]
   [minorodata.app.handlers :refer [dispatch]]
   [minorodata.app.utils :refer [get-dag-id sandbox-dag-id?]]
   [minorodata.pipeline-dag.layout :as layout :refer [parse-raw-edge
                                                      parse-raw-node]]
   [minorodata.sandbox.model :refer [->pipeline-transform-unsaved]]
   [minorodata.sandbox.subs :refer [pipeline-editor-local-storage-key]]
   [minorodata.utils :refer [find-by-field]]
   [re-frame.core :as re-frame :refer [reg-event-fx]]
   [re-posh.core :as re-posh]))

(def empty-tab
  {:id       "e"
   :type     "empty"
   :name     "New tab"
   :group-id ""
   :preview  true})

(def empty-state
  {:active-tab-id (:id empty-tab)
   :sidebar       {:tab "transform"
                   :open true}
   :panel         {:sizes                    [85 "50%" "50%"]
                   :horizontal-sizes         [280 99999 480]
                   :left-sidebar-collapsed?  false
                   :right-sidebar-collapsed? false
                   :collapsed-columns        {}
                   :row-count                200}
   :live-preview  true
   :tabs          [empty-tab]})

(def reset-preview {:tab-preview-group-id         nil
                    :tab-preview-type             nil
                    :tab-preview-id               nil
                    :tab-preview-node-metadata-id nil})

(defn find-tab-by-id [tabs tab-id]
  (find-by-field tabs :id tab-id))

(defn prune-empty
  "Drops nil/empty values from a persisted state blob so the `empty-state` default
  applies instead of a half-written one (e.g. a stored `:tabs []`)."
  [m]
  (when (map? m)
    (into {} (remove (fn [[_ v]] (or (nil? v) (and (coll? v) (empty? v)))) m))))

;; Default DAG view filters for a brand-new tab: both directions, trimmed to the
;; anchor's own lineage, tables shown, no group boxes. Filters are stored per-tab
;; (see :pipeline-editor :tabs) so they persist in tab config and are inherited
;; when a tab is opened from another tab.
;; `:focused?` is on by default because the editor is a SCOPED view — the anchor's
;; ancestors and descendants are what you're editing; cousin branches that merely
;; share a group are noise here (the global pipelines page is where you see
;; everything).
;; `:groups?` drives the shared layout's clustering (see
;; `minorodata.pipeline-dag.layout/do-layout`) — off by default because the graph is
;; already scoped, and the group name on each node card is usually enough.
(def default-dag-filters
  {:direction "all" :focused? true :show-tables? true :groups? false})

(reg-event-fx
 :pipeline-editor.events/persist
 (fn [{:keys [db]}]
   (let [local-storage-key (pipeline-editor-local-storage-key
                            (get-in db [:app :user-id]))]
     {:set-local-storage [local-storage-key (get db :pipeline-editor empty-state)]})))

(def mounted? (atom false))

(reg-event-fx
 :pipeline-editor.events/init
 (fn [{:keys [db]}]
   (reset! mounted? true)
   (let [query              @(re-posh/subscribe [:app-route/prop :app-route/query])
         old-storage-key    (pipeline-editor-local-storage-key nil)
         local-storage-key  (pipeline-editor-local-storage-key
                             (get-in db [:app :user-id]))
         local-storage-data (or (some-> (.getItem js/localStorage local-storage-key)
                                        js/JSON.parse
                                        (js->clj :keywordize-keys true)
                                        prune-empty)
                                ;; It was a bug where user id was always nil, fallback if needed
                                ;; We dont want users to loose their old state
                                (some-> (.getItem js/localStorage old-storage-key)
                                        js/JSON.parse
                                        (js->clj :keywordize-keys true)
                                        prune-empty))
         state              (merge-with
                             (fn [a b] (if (map? a) (merge a b) b))
                             empty-state
                             local-storage-data
                             (when (and (:tab-id query)
                                        (find-tab-by-id (:tabs local-storage-data) (:tab-id query)))
                               {:active-tab-id (:tab-id query)}))
         ;; the persisted active tab may not have survived the restore above
         state              (cond-> state
                              (and (:active-tab-id state)
                                   (not (find-tab-by-id (:tabs state) (:active-tab-id state))))
                              (assoc :active-tab-id (:id (first (:tabs state)))))
         unsaved-changes    (:unsaved-transforms state)]
     {:db         (assoc db :pipeline-editor state)
      :dispatch-n [[:pipeline-editor.events/fetch-mode-data]
                   (when (not-empty unsaved-changes)
                     [:pipeline-transform-unsaved/add-many (mapv ->pipeline-transform-unsaved unsaved-changes)])]})))

(reg-event-fx
 :pipeline-editor.events/fetch-mode-data
 (fn [{:keys [db]}]
   (let [sandbox?          (not (get-in db [:pipeline-editor :live-preview]))
         tab-id            (get-in db [:pipeline-editor :active-tab-id])
         analytics-models? @(re-posh/subscribe [:unleash/is-enabled "fe.analytics-models"])]
     {:dispatch-n (cond-> [[:pipeline-editor.api/fetch-transform-groups]
                           [:connections.api/fetch-sources]
                           ;; extracts are live in both modes
                           [:connections.api/fetch-extract-statuses-for-graph]]
                    analytics-models?
                    ;; instances carry the `model-id -> registry-model` grouping key.
                    (concat [[:analytics-models.api/fetch-models]
                             [:analytics-models.api/fetch-model-registry]
                             [:analytics-models.api/fetch-statuses]])
                    sandbox?
                    (concat [[:pipeline-editor.events/init-sandbox]])
                    (not sandbox?)
                    (concat [[:pipeline-editor.events/on-tab-activated tab-id]]))})))

(reg-event-fx
 :pipeline-editor.events/init-sandbox
 (fn []
   {:async-flow
    {:first-dispatch [:sandbox.api/maybe-create-sandbox]
     :rules          [{:when       :seen?
                       :events     :sandbox.api/maybe-create-sandbox-success
                       :dispatch-n [[:sandbox-updates/add]
                                    [:sandbox.api/fetch-sandbox-updates]
                                    [:sandbox.api/fetch-sandbox-transforms]
                                    [:sandbox.events/watch-execution]
                                    [:pipeline-editor.events/on-tab-activated]]
                       :halt?      true}
                      {:when     :seen?
                       :events   :sandbox.api/maybe-create-sandbox-failure
                       :dispatch [:app.events/add-snack :error "Failed to initialize sandbox"]
                       :halt?    true}]}}))

(def timeout-id (atom nil))

(def watching-execution? (atom false))

;; Generation counter for the poll loop, bumped by `stop-watching-execution!`. A
;; statuses request that was already in flight when the loop was torn down comes
;; back carrying the old epoch, so it can't clear the guard of — or reschedule
;; over the top of — the loop that has since been started fresh.
(def watch-epoch (atom 0))

;; A watch asked for while a cycle is already in flight is DROPPED by the guard in
;; `watch-execution`, not queued, so the ask is remembered here and honoured when that
;; cycle finishes. Otherwise saving a second transform while the first save's statuses
;; fetch is outstanding loses the second watch outright: that fetch went out before the
;; second evaluation existed, so it can come back with no QUEUED rows, release the
;; guard and schedule nothing — leaving the second transform's node frozen at the
;; status it had when it was saved, until some unrelated trigger starts a new watch.
(def rewatch-requested? (atom false))

(def execution-poll-ms 5000)
(def execution-retry-poll-ms 10000)

(defn- clear-poll-timer! []
  (js/clearTimeout @timeout-id)
  (reset! timeout-id nil))

(defn stop-watching-execution!
  "Tear the poll loop down: drop any pending timer, release the in-flight guard,
   forget any deferred re-watch and invalidate the current epoch so an in-flight
   cycle can't resurrect it."
  []
  (clear-poll-timer!)
  (reset! watching-execution? false)
  (reset! rewatch-requested? false)
  (swap! watch-epoch inc))

(defn schedule-watch-execution! [delay-ms]
  (when-not @timeout-id
    (let [epoch @watch-epoch]
      (reset! timeout-id
              (js/setTimeout
               (fn []
                 (reset! timeout-id nil)
                 (when (and @mounted? (= epoch @watch-epoch))
                   (dispatch [:sandbox.events/watch-execution])))
               delay-ms)))))

;; NOTE: `mounted?` is owned by `init` / `cleanup-unmount` alone — it tracks
;; whether the editor screen is on screen, not whether the poll loop is idle.
;; `cleanup` also runs on a Live/Sandbox switch and on a live-data refresh, both
;; of which continue straight into a fresh sandbox init, so it must only stop the
;; loop — latching `mounted?` off here left every later `watch-execution` a no-op
;; for the rest of the session.
(reg-event-fx
 :pipeline-editor.events/cleanup
 (fn []
   (stop-watching-execution!)
   {:fx [[:dispatch [:sandbox-updates/delete]]]}))

(reg-event-fx
 :pipeline-editor.events/cleanup-unmount
 (fn [{:keys [db]}]
   (reset! mounted? false)
   {:db       (dissoc db :pipeline-editor)
    :dispatch [:pipeline-editor.events/cleanup]}))

(reg-event-fx
 :pipeline-editor.events/refresh-live-data
 (fn []
   {:async-flow
    {:first-dispatch [:pipeline-editor.events/cleanup]
     :rules [{:when :seen?
              :events :pipeline-editor.events/cleanup
              :dispatch [:pipeline-editor.events/init]}
             {:when :seen?
              :events [:pipeline-editor.api/fetch-transform-groups-success
                       :dag.api/fetch-graph-success
                       :dag.api/load-dag-metadata-success]
              :dispatch-n [[:app.events/add-snack :success "Live data refreshed"]
                           [:pipeline-editor.events/sync-tabs]]
              :halt? true}
             {:when :seen-any-of?
              :events [:pipeline-editor.api/fetch-transform-groups-failure
                       :dag.api/fetch-graph-failure
                       :dag.api/load-dag-metadata-failure]
              :dispatch [:app.events/add-snack :error "Failed to refresh live data"]
              :halt? true}]}}))

(reg-event-fx
 :pipeline-editor.events/set-panel-sizes
 (fn [{:keys [db]} [_ sizes]]
   {:db (-> db
            (assoc-in [:pipeline-editor :panel :sizes] sizes))
    :fx [[:dispatch [:pipeline-editor.events/persist]]]}))

(reg-event-fx
 :pipeline-editor.events/set-horizontal-sizes
 (fn [{:keys [db]} [_ sizes]]
   (let [sizes   (->> sizes
                      (take 3)
                      (mapv #(int (js/Math.round %))))
         current (get-in db [:pipeline-editor :panel :horizontal-sizes])]
     (if (= sizes current)
       {}
       {:db (assoc-in db [:pipeline-editor :panel :horizontal-sizes] sizes)
        :fx [[:dispatch [:pipeline-editor.events/persist]]]}))))

(reg-event-fx
 :pipeline-editor.events/toggle-left-sidebar
 (fn [{:keys [db]} _]
   {:db (update-in db [:pipeline-editor :panel :left-sidebar-collapsed?] not)
    :fx [[:dispatch [:pipeline-editor.events/persist]]]}))

(reg-event-fx
 :pipeline-editor.events/toggle-right-sidebar
 (fn [{:keys [db]} _]
   {:db (update-in db [:pipeline-editor :panel :right-sidebar-collapsed?] not)
    :fx [[:dispatch [:pipeline-editor.events/persist]]]}))

(reg-event-fx
 :pipeline-editor.events/toggle-picker-column
 (fn [{:keys [db]} [_ column]]
   ;; keyword key so it survives the JSON localStorage round-trip (js->clj :keywordize-keys)
   {:db (update-in db [:pipeline-editor :panel :collapsed-columns (keyword column)] not)
    :fx [[:dispatch [:pipeline-editor.events/persist]]]}))

(reg-event-fx
 :pipeline-editor.events/set-row-count
 (fn [{:keys [db]} [_ count]]
   {:db (-> db
            (assoc-in [:pipeline-editor :panel :row-count] count))
    :fx [[:dispatch [:pipeline-editor.events/persist]]]}))

;; One-shot request for which tab the details pane opens on, written by the canvas's
;; right-click `Preview → Logs` (see `pipeline-editor.dag/preview!`) and consumed by
;; the details pane the preview lands on. It is a request rather than the tab itself
;; so the pane keeps owning its tab state: asking again re-opens the tab even when the
;; same node is previewed and its tab was switched away from in between.
;;
;; It is ADDRESSED to a node (`{:id :tab}`), and a pane only consumes a request
;; carrying its own id, because writing the request is not the same as a pane
;; mounting to consume it. Previewing an extract that doesn't exist in the sandbox
;; renders the empty state instead of the tabs (see `details.extracts/extract-details`),
;; so the request outlives the click — and an unaddressed one would then be eaten by
;; whichever pane mounted next, jumping an unrelated node to Logs. The same applies
;; whenever a transform pane and an extract pane are both mounted.
(reg-event-fx
 :pipeline-editor.events/request-details-tab
 (fn [{:keys [db]} [_ {:keys [id tab]}]]
   {:db (if tab
          (assoc-in db [:pipeline-editor :details-tab-request] {:id id :tab tab})
          (update db :pipeline-editor dissoc :details-tab-request))}))

(reg-event-fx
 :pipeline-editor.events/show-sandbox-unit-tests
 (fn [{:keys [db]} [_ props]]
   {:db (-> db
            (assoc-in [:pipeline-editor :right-sidebar-view] :unit-tests)
            (assoc-in [:pipeline-editor :right-sidebar-unit-tests] props))}))

(reg-event-fx
 :pipeline-editor.events/reset-right-sidebar-view
 (fn [{:keys [db]} _]
   {:db (-> db
            (assoc-in [:pipeline-editor :right-sidebar-view] :summary)
            (update :pipeline-editor dissoc :right-sidebar-unit-tests))}))

(defn should-fetch-dag? [tab]
  (and tab
       (not (#{"empty"} (:type tab)))))

(reg-event-fx
 :pipeline-editor.events/on-tab-activated
 (fn [{:keys [db]} [_ tab-id]]
   (let [tab-id   (or tab-id (get-in db [:pipeline-editor :active-tab-id]))
         sandbox? (not (get-in db [:pipeline-editor :live-preview]))
         tab      (find-tab-by-id (get-in db [:pipeline-editor :tabs]) tab-id)
         ;; Seed the DAG view from the tab's own persisted filters (falling back
         ;; to the defaults for older tabs saved before filters were tab-scoped).
         filters  (or (:filters tab) default-dag-filters)]
     {:dispatch-n [[:pipeline-editor.events/set-dag-filter
                    {:params (merge {:id (get-dag-id tab-id sandbox?)} filters)}]
                   (when (should-fetch-dag? tab)
                     [:sandbox.events/fetch-and-layout-dag
                      {:anchor-id   tab-id
                       :anchor-type (:type tab)
                       :sandbox?    sandbox?}])
                   (when (= "table" (:type tab))
                     [:pipeline-editor.events/fetch-full-table {:params {:id tab-id}}])
                   ;; Load the extract (and its siblings) for the details panel —
                   ;; the tab's group-id is the parent source-id.
                   (when (= "extract" (:type tab))
                     [:connections.api/fetch-extracts {:source-id (:group-id tab) :replace true}])]})))

;; -----------------------------------------------------------------------------
;; DAG view filtering (pure) — applied before layout in relayout-dag.
;; -----------------------------------------------------------------------------

(defn- reachable
  "BFS from `anchor-id` over `edges`. `:upstream` walks target→source (ancestors);
   `:downstream` walks source→target (descendants). Result includes the anchor."
  [anchor-id edges direction]
  (let [[from-k to-k] (case direction
                        :upstream   [:target :source]
                        :downstream [:source :target])
        index         (group-by from-k edges)]
    (loop [visited  #{}
           frontier #{anchor-id}]
      (if (empty? frontier)
        visited
        (let [visited' (into visited frontier)
              nxt      (->> frontier
                            (mapcat #(map to-k (get index % [])))
                            (remove visited')
                            set)]
          (recur visited' nxt))))))

(defn- bypass-tables
  "Drops every non-anchor `table` node and reconnects the nodes it linked, so a
   transform→table→transform chain becomes a direct transform→transform edge.
   The anchor is always kept (e.g. a table page's own table)."
  [nodes edges anchor-id]
  (let [removed (into #{}
                      (comp (filter #(= "table" (:type %)))
                            (map :id)
                            (remove #(= anchor-id %)))
                      nodes)]
    (if (empty? removed)
      {:nodes nodes :edges edges}
      (let [kept       (filterv #(not (removed (:id %))) nodes)
            out-index  (group-by :source edges)
            ;; Walk forward from a kept node, tunnelling through removed nodes,
            ;; and collect the first kept node reached on each branch.
            successors (fn [start-id]
                         (loop [stack (map :target (get out-index start-id []))
                                seen  #{}
                                acc   #{}]
                           (if (empty? stack)
                             acc
                             (let [t (first stack) more (rest stack)]
                               (cond
                                 (seen t)    (recur more seen acc)
                                 (removed t) (recur (concat (map :target (get out-index t [])) more)
                                                    (conj seen t) acc)
                                 :else       (recur more (conj seen t) (conj acc t)))))))
            new-edges  (->> kept
                            (mapcat (fn [{:keys [id]}]
                                      (map (fn [tgt]
                                             {:id (str id "-" tgt) :source id :target tgt})
                                           (successors id))))
                            distinct
                            vec)]
        {:nodes kept :edges new-edges}))))

(defn- kept-ids
  "Node-id set to keep for the given direction + focus. `all` keeps everything
   unless `focused?`, which trims to the anchor's own lineage (ancestors ∪
   descendants), dropping cousin branches. `upstream`/`downstream` are inherently
   focused on the anchor's lineage."
  [nodes edges anchor-id {:keys [direction focused?]}]
  (case direction
    "upstream"   (reachable anchor-id edges :upstream)
    "downstream" (reachable anchor-id edges :downstream)
    ;; "all" / nil
    (if focused?
      (c.set/union (reachable anchor-id edges :upstream)
                   (reachable anchor-id edges :downstream))
      (into #{} (map :id) nodes))))

(defn filter-dag
  "Pure. Produces the `{:nodes :edges}` (unpositioned) to lay out for the current
   filters: optionally bypasses tables, then trims to the direction/focus reach."
  [nodes edges anchor-id filters]
  (let [{:keys [direction focused? show-tables?]} (merge default-dag-filters filters)
        {:keys [nodes edges]} (if show-tables?
                                {:nodes nodes :edges edges}
                                (bypass-tables nodes edges anchor-id))
        keep? (kept-ids nodes edges anchor-id {:direction direction :focused? focused?})]
    {:nodes (filterv #(keep? (:id %)) nodes)
     :edges (filterv #(and (keep? (:source %)) (keep? (:target %))) edges)}))

;; -----------------------------------------------------------------------------
;; DAG layout — store the full parsed graph once, then re-lay-out the filtered
;; view whenever the filters change (so a trimmed graph gets compacted, no gaps).
;; -----------------------------------------------------------------------------

(reg-event-fx
 :sandbox.events/store-raw-dag
 (fn [{:keys [db]} [_ {:keys [dag-id anchor-id nodes edges]}]]
   (let [parsed-nodes (mapv parse-raw-node nodes)
         parsed-edges (mapv parse-raw-edge edges)
         ;; Source the filters from the tab config in re-frame db — a re-posh
         ;; subscription here could still read the pre-update entity, so we can't
         ;; rely on relayout-dag reading them back. The DAG's anchor is its tab.
         tab          (find-tab-by-id (get-in db [:pipeline-editor :tabs]) anchor-id)
         filters      (or (:filters tab) default-dag-filters)]
     {:fx [[:dispatch [:pipeline-dag/update [:pipeline-dag/id dag-id]
                       ;; Write :filters alongside the graph so the entity keeps
                       ;; them after the fetch — the filter dropdown reads them
                       ;; back and would otherwise snap to the defaults on load.
                       {:pipeline-dag/anchor-id anchor-id
                        :pipeline-dag/nodes     parsed-nodes
                        :pipeline-dag/edges     parsed-edges
                        :pipeline-dag/filters   filters}]]
           ;; Pass the parsed graph and filters straight through — a subscription
           ;; in the same event chain could still read the pre-update entity.
           [:dispatch [:sandbox.events/relayout-dag {:dag-id    dag-id
                                                     :anchor-id anchor-id
                                                     :nodes     parsed-nodes
                                                     :edges     parsed-edges
                                                     :filters   filters}]]]})))

;; For data that changes the layout itself, not a node's contents.
(reg-event-fx
 :sandbox.events/relayout-all
 (fn [_ _]
   {:dispatch-n (mapv (fn [dag] [:sandbox.events/relayout-dag
                                 {:dag-id (:pipeline-dag/id dag)}])
                      @(re-posh/subscribe [:pipeline-dag/pull-many]))}))

(def sandbox-no-run-history
  "Node types that carry no execution history in the sandbox, so their cards drop
   the last-run row (see `minorodata.pipeline-dag.layout/size-node`). A sandbox
   transform's state comes from the separate sandbox execution statuses — the node's
   status bar colour, plus the error tooltip when it failed — so its row only ever
   read \"Never executed\", and models never execute in the sandbox at all. Extracts
   are not sandboxed, so they keep the live run they actually had."
  #{"transform" "model"})

(reg-event-fx
 :sandbox.events/relayout-dag
 (fn [{:keys [db]} [_ {:keys [dag-id filters nodes edges anchor-id]}]]
   ;; Prefer explicitly-passed graph/anchor (fresh from store-raw-dag); otherwise
   ;; read the persisted entity (filter-change path, where it's committed already).
   (let [dag       (when (or (nil? nodes) (nil? anchor-id))
                     @(re-posh/subscribe [:pipeline-dag/pull [:pipeline-dag/id dag-id]]))
         raw-nodes (or nodes (:pipeline-dag/nodes dag))
         raw-edges (or edges (:pipeline-dag/edges dag))
         anchor-id (or anchor-id (:pipeline-dag/anchor-id dag))
         filters   (or filters (:pipeline-dag/filters dag) default-dag-filters)
         ;; Read the mode off the dag-id, not the editor's current mode — the live
         ;; graph is also laid out from outside the editor (the read-only pipeline
         ;; preview in the extract drawer), where the editor may sit in sandbox mode.
         sandbox?  (sandbox-dag-id? dag-id)]
     (when (seq raw-nodes)
       (let [{:keys [nodes edges]} (filter-dag raw-nodes raw-edges anchor-id filters)]
         (-> (layout/do-layout {:nodes nodes :edges edges}
                               ;; The editor renders the compact icon-only table
                               ;; pill, so tables lay out at pill size, and it lays
                               ;; out `:dense` — narrower cards, tighter gaps —
                               ;; because it's a scoped view in a panel, not the
                               ;; whole-warehouse page.
                               {:groups?         (:groups? filters)
                                :density         :dense
                                :table-shape     :pill
                                :no-run-history  (when sandbox? sandbox-no-run-history)
                                :model->registry (when (:groups? filters)
                                                   (layout/model-id->registry-model))
                                :user-id         (get-in db [:app :user-id])})
             (.then (fn [result]
                      (dispatch [:sandbox.events/run-layout-success
                                 {:dag-id dag-id :result result}])))
             (.catch (fn [error]
                       (dispatch [:sandbox.events/run-layout-failure error]))))))
     {}))) ;; layout is async — the promise dispatches the result

(reg-event-fx
 :sandbox.events/run-layout-success
 (fn [_ [_ {:keys [dag-id result]}]]
   {:dispatch [:pipeline-dag/update [:pipeline-dag/id dag-id]
               {:pipeline-dag/view-nodes (:nodes result)
                :pipeline-dag/view-edges (:edges result)}]}))

(reg-event-fx
 :sandbox.events/run-layout-failure
 (fn [_ [_ error]]
   (js/console.error "[Layout fail]:" error)
   {}))

(reg-event-fx
 :sandbox.events/fetch-and-layout-dag
 (fn [_ [_ {:keys [anchor-id anchor-type sandbox?]}]]
   (let [dag-id     (get-dag-id anchor-id sandbox?)
         fetch-ev   (if sandbox? :sandbox.api/fetch-sandbox-dag :sandbox.api/fetch-live-dag)
         success-ev (if sandbox?
                      :sandbox.api/fetch-sandbox-dag-success
                      :sandbox.api/fetch-live-dag-success)
         failure-ev (if sandbox?
                      :sandbox.api/fetch-sandbox-dag-failure
                      :sandbox.api/fetch-live-dag-failure)]
     {:async-flow
      {:first-dispatch [fetch-ev {:anchor-id anchor-id :anchor-type anchor-type}]
       :rules
       [{:when        :seen?
         :events      success-ev
         :dispatch-fn (fn [[_ _ _ resp]]
                        [[:sandbox.events/store-raw-dag
                          {:dag-id    dag-id
                           :anchor-id anchor-id
                           :nodes  (mapv (fn [n]
                                           (cond-> n
                                             sandbox? (c.set/rename-keys {:node_id :node-id})
                                             sandbox? (update :metadata #(c.set/rename-keys % {:group_id :group-id
                                                                                               :source_id :source-id
                                                                                               :source_name :source-name
                                                                                               :transform_id :transform-id
                                                                                               :extract_id :extract-id
                                                                                               :model_id :model-id
                                                                                               :model_type :model-type}))))
                                         (:nodes resp))
                           :edges  (:edges resp)}]])
         :halt?       true}
        {:when   :seen?
         :events failure-ev
         :halt?  true}]}})))

(reg-event-fx
 :pipeline-editor.events/open-tab
 (fn [{:keys [db]} [_ tab]]
   (let [tabs       (get-in db [:pipeline-editor :tabs])
         existing?  (find-tab-by-id tabs (:id tab))
         active-tab (find-tab-by-id tabs (get-in db [:pipeline-editor :active-tab-id]))
         ;; A new tab copies the filters of the tab it's opened from (e.g. node
         ;; double-click or "open in new tab"). Opening from the empty "New tab"
         ;; view — which carries no filters — falls back to the defaults.
         tab        (cond-> tab
                      (not existing?)
                      (assoc :filters (or (:filters active-tab) default-dag-filters)))]
     {:db (cond-> db
            (not existing?)
            (update-in [:pipeline-editor :tabs] #(into [] (concat (remove :preview %) [tab])))
            existing?
            (update-in [:pipeline-editor :tabs]
                       (fn [tabs] (mapv (fn [t] (if (= (:id t) (:id tab)) (dissoc t :preview) t)) tabs)))
            true
            (assoc-in [:pipeline-editor :active-tab-id] (:id tab)))
      :fx [[:dispatch [:pipeline-editor.events/persist]]
           [:dispatch [:app.events/navigate-query reset-preview]]
           [:dispatch [:pipeline-editor.events/on-tab-activated (:id tab)]]]})))

(reg-event-fx
 :pipeline-editor.events/navigate-and-open-tab
 ;; Open `tab` in the Pipeline Editor from anywhere in the app. When the editor
 ;; is already mounted we open the tab directly; otherwise we navigate there
 ;; first and wait for `:pipeline-editor.events/init` before opening the tab —
 ;; `init` does a full overwrite of the `:pipeline-editor` db key, so opening
 ;; eagerly would be clobbered.
 (fn [{:keys [db]} [_ tab]]
   (let [open-tab [:pipeline-editor.events/open-tab tab]]
     (if (:pipeline-editor db)
       {:dispatch open-tab}
       {:async-flow
        {:first-dispatch [:app.events/navigate :pipeline-editor]
         :rules          [{:when     :seen?
                           :events   :pipeline-editor.events/init
                           :dispatch open-tab
                           :halt?    true}]}}))))

(reg-event-fx
 :pipeline-editor.events/select-tab
 (fn [{:keys [db]} [_ tab-id]]
   {:db (assoc-in db [:pipeline-editor :active-tab-id] tab-id)
    :fx [[:dispatch [:pipeline-editor.events/persist]]
         [:dispatch [:app.events/navigate-query reset-preview]]
         [:dispatch [:pipeline-editor.events/on-tab-activated tab-id]]]}))

(reg-event-fx
 :pipeline-editor.events/close-tab
 (fn [{:keys [db]} [_ tab-id]]
   (let [tabs             (get-in db [:pipeline-editor :tabs])
         active-tab-id    (get-in db [:pipeline-editor :active-tab-id])
         closing-active?  (= active-tab-id tab-id)
         idx              (first (keep-indexed (fn [i t] (when (= (:id t) tab-id) i)) tabs))
         new-tabs         (filterv #(not= (:id %) tab-id) tabs)
         new-tabs         (if (empty? new-tabs) [empty-tab] new-tabs)
         new-active-id    (if closing-active?
                            (:id (or (get new-tabs (max 0 (dec (or idx 0))))
                                     (first new-tabs)))
                            active-tab-id)]
     {:db (-> db
              (assoc-in [:pipeline-editor :tabs] new-tabs)
              (assoc-in [:pipeline-editor :active-tab-id] new-active-id))
      :fx (cond-> [[:dispatch [:pipeline-editor.events/persist]]
                   [:dispatch [:pipeline-editor.events/purge-local-transform tab-id]]
                   [:dispatch [:pipeline-dag/delete [:pipeline-dag/id (get-dag-id tab-id true)]]]
                   [:dispatch [:pipeline-dag/delete [:pipeline-dag/id (get-dag-id tab-id false)]]]]
            closing-active?
            (conj [:dispatch [:app.events/navigate-query reset-preview]]))})))

(reg-event-fx
 :pipeline-editor.events/sync-tabs
 (fn [{:keys [db]}]
   (let [sandbox?     (not (get-in db [:pipeline-editor :live-preview]))
         pipeline-transforms (when (not sandbox?) @(re-posh/subscribe [:pipeline-transform/pull-many]))
         tab-name     (fn [id]
                        (if sandbox?
                          (-> @(re-posh/subscribe [:sandbox-transform/pull [:sandbox-transform/master-id id]])
                              :sandbox-transform/name)
                          (some #(when (and (= (:pipeline-transform/master-id %) id)
                                            (not (:pipeline-transform/stale %)))
                                   (:pipeline-transform/name %))
                                pipeline-transforms)))
         tabs         (get-in db [:pipeline-editor :tabs])
         updated-tabs (mapv (fn [{:keys [type id] :as tab}]
                              (if (not= type "transform")
                                tab
                                (let [name (tab-name id)]
                                  (cond-> tab name (assoc :name name)))))
                            tabs)]
     {:db (assoc-in db [:pipeline-editor :tabs] updated-tabs)
      :fx [[:dispatch [:pipeline-editor.events/persist]]]})))

(reg-event-fx
 :pipeline-editor.events/close-all-tabs
 (fn [{:keys [db]}]
   {:db (-> db
            (assoc-in [:pipeline-editor :tabs] [empty-tab])
            (assoc-in [:pipeline-editor :active-tab-id] nil))
    :fx [[:dispatch [:app.events/navigate-query reset-preview]]
         [:dispatch [:pipeline-editor.events/persist]]]}))

(reg-event-fx
 :pipeline-editor.events/switch-mode
 (fn [{:keys [db]} [_ preview]]
   {:db (update-in db [:pipeline-editor :live-preview] #(if (some? preview) preview (not %)))
    :fx [[:dispatch [:pipeline-editor.events/cleanup]]
         [:dispatch [:pipeline-editor.events/fetch-mode-data]]
         [:dispatch [:pipeline-editor.events/sync-tabs]]
         [:dispatch [:pipeline-editor.events/persist]]]}))

(reg-event-fx
 :pipeline-editor.events/create-and-open-transform
 (fn [_ [_ {:keys [group-id transform on-success-ev]}]]
   {:async-flow
    {:first-dispatch [:sandbox.api/create-sandbox-transform {:group-id  group-id
                                                             :transform transform}]
     :rules          [{:when     :seen?
                       :events   :sandbox.api/create-sandbox-transform-success
                       :dispatch-fn  (fn [[_ _ _ resp]]
                                       (concat
                                        [[:pipeline-editor.events/navigate-and-open-tab
                                          {:id       (:master_id resp)
                                           :group-id group-id
                                           :type     "transform"
                                           :name     (:name resp)}]]
                                        (if (fn? on-success-ev)
                                          (on-success-ev resp)
                                          on-success-ev)))
                       :halt?    true}]}}))
(reg-event-fx
 :pipeline-editor.events/create-and-select-transform
 (fn [{:keys [db]} [_ {:keys [group-id transform]}]]
   (let [active-tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/create-sandbox-transform {:group-id  group-id
                                                               :transform transform}]
       :rules          [{:when :seen?
                         :events :sandbox.api/create-sandbox-transform-success
                         :dispatch-fn (fn [[_ _ _ resp]]
                                        [[:sandbox.api/fetch-sandbox-transforms {:anchor-id active-tab-id}]
                                         [:app.events/navigate-query {:tab-preview-type     "transform"
                                                                      :tab-preview-id       (:master_id resp)
                                                                      :tab-preview-group-id (:group_id resp)}]
                                         [:pipeline-editor.events/on-tab-activated active-tab-id]])
                         :halt? true}]}})))

(defn new-transform-command [{:keys [input-table output-table fields]}]
  (let [table-fields (if (empty? fields)
                       ""
                       (str "(\n  " (str/join ",\n  " (map #(str (:column-name %) " " (:type-name %)) fields)) "\n) "))
        select-fields (if (empty? fields)
                        "*"
                        (str/join ", " (map :column-name fields)))]
    (str (when output-table
           (str "CREATE OR REPLACE TABLE " output-table table-fields " AS\n"))
         "SELECT " select-fields " FROM " input-table ";")))

(reg-event-fx
 :pipeline-editor.events/create-and-select-transform-with-table
 (fn [_ [_ {:keys [group-id transform-name input-table output-table on-success]}]]
   (let [[table schema catalog] (reverse (str/split input-table #"\."))]
     {:async-flow
      {:first-dispatch [:sql-console.api/fetch-metadata {:object "columns"
                                                         :catalog catalog
                                                         :schema  schema
                                                         :table   table}]
       :rules
       [{:when     :seen?
         :events   :sql-console.api/fetch-metadata-success
         :dispatch-fn (fn [[_ _ _ result]]
                        (dispatch [:pipeline-editor.events/create-and-select-transform
                                   {:group-id  group-id
                                    :transform {:name transform-name
                                                :command (new-transform-command
                                                          {:input-table input-table
                                                           :output-table output-table
                                                           :fields result})
                                                :output-tables [output-table]
                                                :input-tables  [input-table]}}])
                        (when (fn? on-success) (on-success)))
         :halt?    true}]}})))


(reg-event-fx
 :pipeline-editor.events/create-and-select-transform-sandbox
 (fn [_ [_ {:keys [group-id transform-name input-table on-success]}]]
   (let [table @(re-posh/subscribe [:sandbox-table/pull [:sandbox-table/id input-table]])
         fields (mapv #(c.set/rename-keys % {:column :column-name
                                             :column_name :column-name
                                             :type :type-name})
                      (:sandbox-table/columns table))]
     (when (fn? on-success) (on-success))
     {:dispatch [:pipeline-editor.events/create-and-select-transform
                 {:group-id  group-id
                  :transform {:name transform-name
                              :command (new-transform-command
                                        {:input-table input-table
                                         :fields fields})}}]})))

(reg-event-fx
 :pipeline-editor.events/set-dag-filter
 (fn [{:keys [db]} [_ {{:keys [id] :as params} :params}]]
   ;; Merge the provided keys (:direction / :focused? / :show-tables?) over any
   ;; existing filters so a partial update (e.g. tab activation passing only :id)
   ;; preserves the rest. The active tab's :filters are kept in sync with the DAG
   ;; entity, so read the existing filters straight from db (guaranteed current in
   ;; the handler, and avoids an undisposed re-posh reaction). Persist onto the
   ;; active tab (so the filters live in tab config across reloads and can be
   ;; inherited by tabs opened from it), then re-lay-out the filtered view.
   (let [active-tab-id (get-in db [:pipeline-editor :active-tab-id])
         existing      (:filters (find-tab-by-id (get-in db [:pipeline-editor :tabs]) active-tab-id))
         filters       (merge default-dag-filters existing (dissoc params :id))]
     {:db (update-in db [:pipeline-editor :tabs]
                     (fn [tabs]
                       (mapv (fn [t]
                               (cond-> t
                                 (= (:id t) active-tab-id) (assoc :filters filters)))
                             tabs)))
      :fx [[:dispatch [:pipeline-dag/update [:pipeline-dag/id id]
                       {:pipeline-dag/filters filters}]]
           [:dispatch [:sandbox.events/relayout-dag {:dag-id id :filters filters}]]
           [:dispatch [:pipeline-editor.events/persist]]]})))

(reg-event-fx
 :pipeline-editor.events/fetch-full-table
 (fn [_ [_ props]]
   (let [sandbox? @(re-posh/subscribe [:sandbox.subs/enabled?])
         ev (if sandbox?
              :sandbox.api/fetch-sandbox-table-success
              :pipeline-editor.api/fetch-live-table-success)]
     {:async-flow
      {:first-dispatch [:sandbox.api/fetch-table props]
       :rules          [{:when     :seen?
                         :events   ev
                         :dispatch-n [[:sandbox.api/fetch-table-preview props]]
                         :halt?    true}]}})))

(defn find-index [coll item-id]
  (some->> (map-indexed (fn [idx item] (when (= (:id item) item-id) idx)) coll)
           (filter some?)
           first))

(defn update-item [coll item-id f]
  (let [idx (find-index coll item-id)]
    (if idx
      (update coll idx f)
      (concat coll [(f {:id item-id})]))))

(reg-event-fx
 :pipeline-editor.events/update-local-transform
 (fn [{:keys [db]} [_ id command]]
   {:db (update-in db [:pipeline-editor :unsaved-transforms]
                   (fn [items]
                     (into [] (update-item items id #(assoc % :command command)))))
    :fx [[:dispatch [:pipeline-transform-unsaved/update [:pipeline-transform-unsaved/id id]
                     {:pipeline-transform-unsaved/command command}]]
         [:dispatch [:pipeline-editor.events/persist]]]}))

(reg-event-fx
 :pipeline-editor.events/purge-local-transform
 (fn [{:keys [db]} [_ id]]
   {:db (update-in db [:pipeline-editor :unsaved-transforms]
                   (fn [items]
                     (into [] (remove #(= (:id %) id) items))))
    :fx [[:dispatch [:pipeline-transform-unsaved/delete [:pipeline-transform-unsaved/id id]]]
         [:dispatch [:pipeline-editor.events/persist]]]}))


(reg-event-fx
 :pipeline-editor.events/purge-local-transforms
 (fn [{:keys [db]}]
   (let [stored-transforms @(re-frame/subscribe [:pipeline-transform-unsaved/pull-many])]
     {:db (assoc-in db [:pipeline-editor :unsaved-transforms] [])
      :fx (concat
           (mapv (fn [{:pipeline-transform-unsaved/keys [id]}]
                   [:dispatch [:pipeline-transform-unsaved/delete [:pipeline-transform-unsaved/id id]]])
                 stored-transforms)
           [[:dispatch [:pipeline-editor.events/persist]]])})))

(reg-event-fx
 :pipeline-editor.events/add-table-to-transform
 (fn [_ [_ {:keys [id catalog schema table]}]]
   (let [unsaved-transform @(re-posh/subscribe [:pipeline-transform-unsaved/pull [:pipeline-transform-unsaved/id id]])
         command (or (:pipeline-transform-unsaved/command unsaved-transform)
                     @(re-posh/subscribe [:pipeline-transform/prop
                                          [:pipeline-transform/id id]
                                          :pipeline-transform/command]))
         new-command (str "WITH " table " AS (\n  SELECT * FROM " catalog "." schema "." table "\n)\n"
                          command)]
     {:fx [(if unsaved-transform
             [:dispatch [:pipeline-transform-unsaved/update [:pipeline-transform-unsaved/id id]
                         {:pipeline-transform-unsaved/command new-command}]]
             [:dispatch [:pipeline-transform-unsaved/add (->pipeline-transform-unsaved {:id id
                                                                                        :command new-command})]])
           [:dispatch [:sandbox.api/update-transform {:id id}]]]})))

(reg-event-fx
 :pipeline-editor.events/create-group
 (fn [_ [_ {:keys [group-name on-success]}]]
   {:async-flow
    {:first-dispatch [:group.api/create-group {:group-name group-name}]
     :rules          [{:when     :seen?
                       :events   :group.api/create-group-success
                       :dispatch-fn (fn [[_ _ _ data]]
                                      (on-success (:id data))
                                      nil)
                       :halt?    true}]}}))

(reg-event-fx
 :sandbox.events/reset-sandbox
 (fn [{:keys [db]} [_ props]]
   (let [tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/reset-sandbox props]
       :rules          [{:when :seen?
                         :events :sandbox.api/reset-sandbox-success
                         :dispatch-n  [[:sandbox.api/fetch-sandbox-transforms {:anchor-id tab-id}]
                                       [:sandbox.api/fetch-sandbox-updates]
                                       [:sandbox.api/maybe-create-sandbox]
                                       [:sandbox.events/refresh-sandbox-tables]
                                       [:pipeline-editor.events/purge-local-transforms]
                                       [:pipeline-editor.events/on-tab-activated]
                                       [:app.events/navigate-query reset-preview]]
                         :halt? true}]}})))

(reg-event-fx
 :sandbox.events/rebase-sandbox
 (fn [{:keys [db]}]
   (let [tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/rebase-version]
       :rules          [{:when :seen?
                         :events :sandbox.api/rebase-version-success
                         :dispatch-n [[:sandbox.api/fetch-sandbox-transforms {:anchor-id tab-id}]
                                      [:sandbox.events/refresh-sandbox-tables]
                                      [:sandbox.api/fetch-sandbox-updates]
                                      [:sandbox.api/maybe-create-sandbox]
                                      [:pipeline-editor.events/on-tab-activated]
                                      [:app.events/navigate-query reset-preview]]
                         :halt? true}]}})))

(reg-event-fx
 :sandbox.events/commit-sandbox
 (fn [{:keys [db]} [_ props]]
   (let [tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/commit-version props]
       :rules          [{:when :seen?
                         :events :sandbox.api/commit-version-success
                         :dispatch-n [[:pipeline-editor.events/purge-local-transforms]
                                      [:sandbox.api/fetch-live-transforms]
                                      [:sandbox.api/fetch-sandbox-transforms {:anchor-id tab-id}]
                                      [:sandbox.api/fetch-sandbox-updates]
                                      [:pipeline-editor.events/switch-mode true]]
                         :halt? true}]}})))

(reg-event-fx
 :sandbox.events/stash-sandbox
 (fn [{:keys [db]} [_ props]]
   (let [tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/stash-version props]
       :rules          [{:when :seen?
                         :events :sandbox.api/stash-version-success
                         :dispatch-n [[:sandbox.api/fetch-sandbox-transforms {:anchor-id tab-id}]
                                      [:sandbox.api/fetch-versions]
                                      [:sandbox.api/fetch-sandbox-updates]
                                      [:sandbox.events/refresh-sandbox-tables]
                                      [:pipeline-editor.events/on-tab-activated]
                                      [:app.events/navigate-query reset-preview]]
                         :halt? true}]}})))

(reg-event-fx
 :sandbox.events/load-version
 (fn [{:keys [db]} [_ props]]
   (let [tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/load-version props]
       :rules          [{:when :seen?
                         :events :sandbox.api/load-version-success
                         :dispatch-n [[:sandbox.api/fetch-sandbox-transforms {:anchor-id tab-id}]
                                      [:sandbox.api/fetch-versions]
                                      [:sandbox.events/refresh-sandbox-tables]
                                      [:pipeline-editor.events/on-tab-activated]
                                      [:app.events/navigate-query {:popup "review"
                                                                   :version-id nil}]
                                      [:sandbox.api/fetch-sandbox-updates]]
                         :halt? true}]}})))

(reg-event-fx
 :sandbox.events/delete-version
 (fn [_ [_ props]]
   {:async-flow
    {:first-dispatch [:sandbox.api/delete-version props]
     :rules          [{:when :seen?
                       :events :sandbox.api/delete-version-success
                       :dispatch-n [[:sandbox.api/fetch-versions]]
                       :halt? true}]}}))

(reg-event-fx
 :sandbox.events/reset-sandbox-transform
 (fn [{:keys [db]} [_ props]]
   (let [tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/reset-sandbox-transform props]
       :rules          [{:when :seen?
                         :events :sandbox.api/reset-sandbox-transform-success
                         :dispatch-n [[:sandbox.api/fetch-sandbox-transforms {:anchor-id tab-id}]
                                      [:sandbox.events/refresh-sandbox-tables]
                                      [:sandbox.api/fetch-sandbox-updates]
                                      [:pipeline-editor.events/on-tab-activated]
                                      [:app.events/navigate-query reset-preview]]
                         :halt? true}]}})))

(reg-event-fx
 :sandbox.events/delete-sandbox-transform
 (fn [{:keys [db]} [_ {:keys [id] :as props}]]
   (let [tab-id           (get-in db [:pipeline-editor :active-tab-id])
         deleting-active? (= id tab-id)]
     {:async-flow
      {:first-dispatch [:sandbox.api/delete-sandbox-transform props]
       :rules          [{:when :seen?
                         :events :sandbox.api/delete-sandbox-transform-success
                         :dispatch-n (cond-> [[:sandbox.api/fetch-sandbox-transforms
                                               {:anchor-id (when-not deleting-active? tab-id)}]
                                              [:sandbox.events/refresh-sandbox-tables]
                                              [:sandbox.api/fetch-sandbox-updates]
                                              [:app.events/navigate-query reset-preview]]
                                       deleting-active?       (conj [:pipeline-editor.events/close-tab id])
                                       (not deleting-active?) (conj [:pipeline-editor.events/on-tab-activated]))
                         :halt? true}]}})))



(reg-event-fx
 :sandbox.events/refresh-sandbox-tables
 (fn []
   (let [tables @(re-posh/subscribe [:sandbox-table/pull-many])]
     {:dispatch-n (into [[:sandbox.api/fetch-sandbox-tables]]
                        (mapv #(-> [:sandbox-table/delete [:sandbox-table/id (:id %)]])
                              tables))})))

(reg-event-fx
 :sandbox.events/watch-execution
 (fn []
   (cond
     (not @mounted?) nil

     ;; Already out for statuses. That fetch may predate whatever this watch is
     ;; about, so it can't stand in for it — defer the ask instead of dropping it
     ;; (see `rewatch-requested?`) rather than running fetches over each other.
     @watching-execution? (do (reset! rewatch-requested? true) nil)

     :else
     (do
       ;; A caller-triggered watch (transform saved, sandbox reset, version loaded,
       ;; …) supersedes a queued poll: drop the pending timer and go out for
       ;; statuses now, so the new evaluation shows up without waiting out the
       ;; remainder of the interval.
       (clear-poll-timer!)
       (reset! watching-execution? true)
       (let [epoch   @watch-epoch
             finish! (fn [reschedule-ms]
                       ;; Only the current generation may release the guard or keep
                       ;; the loop going — see `watch-epoch`.
                       (when (= epoch @watch-epoch)
                         (reset! watching-execution? false)
                         (let [rewatch? @rewatch-requested?]
                           (reset! rewatch-requested? false)
                           ;; A watch deferred mid-cycle goes out immediately — it may
                           ;; be the only one coming, since a cycle that saw no QUEUED
                           ;; rows schedules nothing.
                           (when-let [ms (if rewatch? 0 reschedule-ms)]
                             (when @mounted?
                               (schedule-watch-execution! ms))))))]
         {:async-flow
          {:first-dispatch [:sandbox.api/fetch-execution-statuses]
           :rules [{:when :seen?
                    :events :sandbox.api/fetch-execution-statuses-success
                    :dispatch-fn (fn [[_ _ _ resp]]
                                   (finish! (when (some #(= (:status %) "QUEUED") resp)
                                              execution-poll-ms))
                                   nil)
                    :halt? true}

                   {:when :seen?
                    :events :sandbox.api/fetch-execution-statuses-failure
                    :dispatch-fn (fn [_]
                                   (finish! execution-retry-poll-ms)
                                   nil)
                    :halt? true}]}})))))

(reg-event-fx
 :sandbox.events/preview-with-delay
 (fn [_ [_ params]]
   {:dispatch-later
    {:dispatch [:sandbox.api/preview-sandbox-sql params]
     :ms 100}}))

(reg-event-fx
 :sandbox.events/update-transform
 (fn [{:keys [db]} [_ {:keys [id updates]}]]
   (let [tab-id (get-in db [:pipeline-editor :active-tab-id])]
     {:async-flow
      {:first-dispatch [:sandbox.api/update-sandbox-transform {:id id :updates updates}]
       :rules          [{:when :seen?
                         :events :sandbox.api/update-sandbox-transform-success
                         :dispatch-n [[:sandbox.api/evaluate-pipelines]
                                      [:sandbox.events/watch-execution]
                                      [:sandbox.api/fetch-sandbox-transforms {:anchor-id tab-id}]
                                      [:sandbox.api/fetch-sandbox-updates]
                                      [:pipeline-editor.events/purge-local-transform id]
                                      [:pipeline-editor.events/on-tab-activated tab-id]]
                         :halt? true}
                        {:when :seen?
                         :events :sandbox.api/update-sandbox-transform-failure
                         :halt? true}]}})))

(reg-event-fx
 :sandbox.events/save-and-preview-sandbox-sql
 (fn [_ [_ {:keys [id sql]}]]
   (let [local-transform @(re-posh/subscribe [:pipeline-transform-unsaved/pull [:pipeline-transform-unsaved/id id]])]
     (if (not-empty local-transform)
       {:async-flow
        {:first-dispatch [:sandbox.events/update-transform {:id id}]
         :rules          [{:when     :seen?
                           :events   :sandbox.api/update-sandbox-transform-success
                           :dispatch [:sandbox.events/preview-with-delay {:id id :sql sql}]
                           :halt?    true}]}}
       {:dispatch [:sandbox.api/preview-sandbox-sql {:id id :sql sql}]}))))
